import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
    getUserProfileCached,
    getProfileMetrics,
    getPlanetInfluences,
    getPersonalityCards,
    getPlanetBlurbs,
    getPersonalityQuoteCached,
    getBackendSessionCached
} from '../services/api';
import api from '../services/api';
import logger from '../utils/logger';
import { useAuth } from './AuthContext';

interface ProfileContextType {
    // Data
    profile: any;
    metrics: any[];
    influences: any[];
    personalityCards: any[];
    planetBlurbs: any[];
    quote: string | null;
    chart: any;

    // Status
    isLoading: boolean;
    error: string | null;

    // Actions
    prefetchAll: (isPolling?: boolean) => Promise<{ hasCards: boolean; hasBlurbs: boolean; } | null | undefined | void>;
    refreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, session, isLoading: authLoading } = useAuth();

    const [profile, setProfile] = useState<any>(null);
    const [metrics, setMetrics] = useState<any[]>([]);
    const [influences, setInfluences] = useState<any[]>([]);
    const [personalityCards, setPersonalityCards] = useState<any[]>([]);
    const [planetBlurbs, setPlanetBlurbs] = useState<any[]>([]);
    const [quote, setQuote] = useState<string | null>(null);
    const [chart, setChart] = useState<any>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Polling ref to avoid infinite loops or overlapping requests
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const isPollingRef = useRef(false);

    const prefetchAll = useCallback(async (isPolling = false) => {
        if (!user) return;
        // If not polling, and data already loaded, skip (unless manual refresh)
        if (!isPolling && dataLoaded) return;

        try {
            if (!isPolling) setIsLoading(true);
            logger.info('ProfileContext', isPolling ? 'Polling for updates...' : 'Prefetching all user data');

            // 1. Basic Profile & Quote (Fastest)
            const [p, q] = await Promise.all([
                getUserProfileCached().catch(e => {
                    logger.warn('ProfileContext', 'Failed to load profile', { error: e });
                    return null;
                }),
                getPersonalityQuoteCached().catch(e => {
                    logger.warn('ProfileContext', 'Failed to load quote', { error: e });
                    return null;
                })
            ]);

            if (p?.data) setProfile(p.data);
            if (q?.quote) setQuote(q.quote);

            // 2. Heavy Data (Metrics, Influences, Cards)
            // We don't block the UI for these if we're just prefetching in background
            // But if this is an explicit load, we wait.

            const [m, inf, pc, pb] = await Promise.all([
                getProfileMetrics().catch(e => ({ metrics: [] })),
                getPlanetInfluences().catch(e => ({ influences: [] })),
                getPersonalityCards().catch(e => ({ cards: [] })),
                getPlanetBlurbs().catch(e => ({ blurbs: [] }))
            ]);

            setMetrics(m.metrics || []);
            setInfluences(inf.influences || []);
            setPersonalityCards(pc.cards || []);
            setPlanetBlurbs(pb.blurbs || []);

            // 3. Chart Data (Slowest)
            try {
                const sessionRes = await getBackendSessionCached();
                const backendUserId = sessionRes.data.user_id;
                if (backendUserId) {
                    const chartRes = await api.get(`/chart/${backendUserId}`);
                    setChart(chartRes.data);
                }
            } catch (e) {
                logger.warn('ProfileContext', 'Failed to load chart', { error: e });
            }

            setDataLoaded(true);
            logger.info('ProfileContext', 'Prefetch complete');

            return {
                hasCards: (pc.cards || []).length > 0,
                hasBlurbs: (pb.blurbs || []).length > 0
            };
        } catch (e: any) {
            logger.error('ProfileContext', 'Error in prefetchAll', e);
            setError(e.message || 'Failed to load profile data');
            return null;
        } finally {
            if (!isPolling) setIsLoading(false);
        }
    }, [user, dataLoaded]);

    const refreshProfile = useCallback(async () => {
        setDataLoaded(false);
        await prefetchAll();
    }, [prefetchAll]);

    // Auto-fetch data when user is authenticated
    useEffect(() => {
        // Wait for auth to finish loading before fetching profile data
        if (!authLoading && user && !dataLoaded && !isLoading) {
            logger.info('ProfileContext', 'User authenticated and auth loaded, auto-fetching profile data');
            prefetchAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, dataLoaded, isLoading, authLoading]);

    // Polling effect: Check if we need to poll for AI content
    useEffect(() => {
        if (!user || !dataLoaded) return;

        const needsPolling =
            (personalityCards.length === 0) ||
            (planetBlurbs.length === 0);

        if (needsPolling && !isPollingRef.current) {
            logger.info('ProfileContext', 'AI content missing, starting polling...');
            isPollingRef.current = true;

            pollingRef.current = setInterval(async () => {
                const result = await prefetchAll(true);

                // If we got the data, stop polling
                if (result && result.hasCards && result.hasBlurbs) {
                    logger.info('ProfileContext', 'AI content received, stopping polling');
                    if (pollingRef.current) {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                    }
                    isPollingRef.current = false;
                }
            }, 5000); // Poll every 5 seconds
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            isPollingRef.current = false;
        };
    }, [user, dataLoaded, personalityCards.length, planetBlurbs.length, prefetchAll]);

    // Reset state on logout
    useEffect(() => {
        if (!user) {
            setProfile(null);
            setMetrics([]);
            setInfluences([]);
            setPersonalityCards([]);
            setPlanetBlurbs([]);
            setQuote(null);
            setChart(null);
            setDataLoaded(false);
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            isPollingRef.current = false;
        }
    }, [user]);

    return (
        <ProfileContext.Provider
            value={{
                profile,
                metrics,
                influences,
                personalityCards,
                planetBlurbs,
                quote,
                chart,
                isLoading,
                error,
                prefetchAll,
                refreshProfile
            }}
        >
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (context === undefined) {
        throw new Error('useProfile must be used within a ProfileProvider');
    }
    return context;
};
